import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { Box, Chip, Tooltip } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { generatePath, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ROUTES from '../../assets/routes'
import { RootState } from '../../store'
import { CommunityScreenMetadata } from '../../proto/generated/screener'
import { Security } from '../../proto/generated/security'
import { getSecurityKey } from '../../util/security'
import OverviewSectionHeader from './OverviewSectionHeader'
import { OverviewSection, OVERVIEW_SECTION_IDS, OVERVIEW_SECTION_ORDER } from './OverviewSectionNav'

const COLLAPSED_CHIP_COUNT = 6

const useStyles = makeStyles()((theme) => ({
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0, 2, 2)
    },
    chip: {
        height: 24,
        fontSize: 11,
        maxWidth: '100%',
        borderRadius: theme.shape.borderRadius,
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1)
        },
        '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover
        }
    },
    moreChip: {
        height: 24,
        fontSize: 11,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.secondary
    }
}))

export type CommunityScreenChipsProps = {
    security: Security | undefined
}

const CommunityScreenChips = ({ security }: CommunityScreenChipsProps) => {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)
    const storeScreens = useSelector((state: RootState) => state.overviewStore.communityScreens)
    const overviewSecurityKey = useSelector((state: RootState) => state.overviewStore.overviewSecurityKey)

    const handleClick = useCallback(
        (screen: CommunityScreenMetadata) => {
            navigate(generatePath(ROUTES.screener, { screenerId: screen.id }))
        },
        [navigate]
    )

    // Hold last committed screens until store key matches the displayed security.
    const committedScreensRef = useRef<CommunityScreenMetadata[]>([])
    const communityScreens = useMemo(() => {
        const securityKey = security ? getSecurityKey(security.exchange, security.code) : null
        if (securityKey && overviewSecurityKey === securityKey) {
            committedScreensRef.current = storeScreens
            return storeScreens
        }
        return committedScreensRef.current
    }, [overviewSecurityKey, security, storeScreens])

    const visibleScreens = useMemo(
        () => (expanded ? communityScreens : communityScreens.slice(0, COLLAPSED_CHIP_COUNT)),
        [communityScreens, expanded]
    )

    if (!communityScreens?.length) return null

    const hiddenCount = communityScreens.length - visibleScreens.length

    return (
        <OverviewSection
            id={OVERVIEW_SECTION_IDS.community}
            label="Community"
            order={OVERVIEW_SECTION_ORDER.community}
        >
        <Box boxShadow={0}>
            <OverviewSectionHeader title="Community Screens" />
            <Box className={classes.chips}>
                {visibleScreens.map((screen) => (
                    <Tooltip
                        key={screen.id}
                        title={screen.description || screen.name}
                        placement="top"
                        arrow
                    >
                        <Chip
                            label={screen.name}
                            size="small"
                            variant="outlined"
                            clickable
                            className={classes.chip}
                            onClick={() => handleClick(screen)}
                            data-event="Community Screen Chip Clicked"
                            data-event-screen-name={screen.name}
                            data-event-source="overview"
                        />
                    </Tooltip>
                ))}
                {hiddenCount > 0 && (
                    <Chip
                        label={`+${hiddenCount}`}
                        size="small"
                        variant="outlined"
                        clickable
                        className={classes.moreChip}
                        onClick={() => setExpanded(true)}
                    />
                )}
            </Box>
        </Box>
        </OverviewSection>
    )
}

export default memo(CommunityScreenChips)
